import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import { Modal } from '../Modal';

import { Button } from '@/components/shared/Button';
import { Icons, IconsTypes } from '@/components/shared/Icons';
import { Image } from '@/components/shared/Image';
import { useToggle } from '@/hooks/useToggle';
import { Colors, IPlantImage } from '@/types';

interface IImageSlider {
  sliderImages: IPlantImage[];
  preSelectedIndex?: number;
  containerClassName?: string;
  name?: string /* page name or content title or gallery name */;

  showDots?: boolean;
  showFooterSlides?: boolean;
  vertical?: boolean;
  allowExpandImage?: boolean;
  aspect?: string;
}

// if you want this dynamic, pass to the props
const DOTS_TO_SHOW = 3;
const LEFT_STYLE_MULTIPLIER = 10 * 3;

export const ImageSlider = ({
  sliderImages = [],
  showDots = true,
  showFooterSlides = true,
  vertical = false,
  preSelectedIndex = 0,
  allowExpandImage = true,
  containerClassName = '',
  name = '',
  aspect = 'aspect-square'
}: IImageSlider) => {
  const router = useRouter();
  const [currentIndex, setCurrentIndex] = useState(preSelectedIndex);
  const [isImageExpanded, expandImage] = useToggle();

  const pageType = router.query.type;

  useEffect(() => {
    // since we could use this slider on the same page
    // like [page].tsx all could be stale and here
    // we're checkeing if it's a new page in order
    // to reset index
    !preSelectedIndex && setCurrentIndex(0);
  }, [pageType, preSelectedIndex]);

  const getNewItem = (isIncreasing: boolean) => {
    if (isIncreasing) {
      if (currentIndex + 1 >= sliderImages.length) {
        setCurrentIndex(0);
        return;
      }

      setCurrentIndex((prev) => prev + 1);
      return;
    }

    if (currentIndex - 1 < 0) {
      setCurrentIndex(sliderImages.length - 1);
      return;
    }

    setCurrentIndex((prevIndex) => prevIndex - 1);

    return;
  };

  const filteredByMain = sliderImages.sort((a, b) => +b.is_main - +a.is_main);

  const selectedItem = filteredByMain[currentIndex] as IPlantImage;

  const handleSelectImage = (img: IPlantImage) => setCurrentIndex(filteredByMain.indexOf(img));

  const moreThanOne = filteredByMain.length > 1;

  const handleOnExpandImage = () => allowExpandImage && expandImage();

  // if it's showing last DOTS_TO_SHOW dots - stop scrolling
  // if you want to make first DOTS_TO_SHOW static, apply the same logic but with
  // the first DOTS_TO_SHOW elements
  const lastElementsIndex = filteredByMain.length - DOTS_TO_SHOW;

  const isLastElements = currentIndex >= lastElementsIndex;

  const dotsLeftPercent = useMemo(() => {
    if (moreThanOne && filteredByMain.length < DOTS_TO_SHOW) return LEFT_STYLE_MULTIPLIER / 2;

    if (isLastElements) return lastElementsIndex * LEFT_STYLE_MULTIPLIER * -1;

    return currentIndex * LEFT_STYLE_MULTIPLIER * -1;
  }, [currentIndex, filteredByMain.length, isLastElements, lastElementsIndex, moreThanOne]);

  return (
    <>
      <div className="w-full">
        <div
          className={`flex flex-col items-center gap-4 overflow-hidden md:h-[610px] ${vertical ? 'md:flex-row' : ''}`}
        >
          <div className={`relative ${aspect} w-full ${vertical ? 'md:w-4/5' : ''}`}>
            <div className={`main-image ${aspect} w-full ${containerClassName} `}>
              <Image
                onClick={handleOnExpandImage}
                className={`h-full w-full ${aspect} rounded-lg ${allowExpandImage ? 'cursor-pointer object-cover' : ''}`}
                optimized={selectedItem.image}
                src={selectedItem.img}
                title={selectedItem.title}
                alt={selectedItem.title || name}
              />
            </div>

            {moreThanOne && (
              <>
                <div className="absolute top-1/2 -translate-y-1/2 px-2">
                  <Button variant={Colors.rounded} onClick={() => getNewItem(false)}>
                    {Icons({ name: IconsTypes.ARROW_LEFT })}
                  </Button>
                </div>
                <div className="absolute right-0 top-1/2 -translate-y-1/2 px-2">
                  <Button variant={Colors.rounded} onClick={() => getNewItem(true)}>
                    {Icons({ name: IconsTypes.ARROW_RIGHT, iconProps: { className: 'text-black-600' } })}
                  </Button>
                </div>
              </>
            )}

            {/* 40px each dot (includes width, margin) */}
            {showDots && moreThanOne && (
              // if DOTS_TO_SHOW would become prop, pass it here as dynamic mulitplier for width
              <div className="absolute bottom-0 left-1/2 h-5 w-full max-w-[130px] -translate-x-1/2 overflow-hidden">
                <div
                  className="absolute flex items-center justify-center transition-all ease-linear"
                  style={{
                    left: `${dotsLeftPercent}%`,
                  }}
                >
                  {[...filteredByMain].map((item) => (
                    <div
                      onClick={() => handleSelectImage(item)}
                      key={item.id}
                      className={`mx-4 mb-3 h-[10px] w-[10px] cursor-pointer rounded-full border border-white ${
                        selectedItem.id === item.id ? 'bg-white' : 'bg-transparent'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          {showFooterSlides && moreThanOne && (
            <div className={`flex h-full md:self-start ${vertical ? 'w-full md:w-1/4' : 'w-full overflow-y-hidden overflow-x-scroll'}`}>
              <div
                className={`flex gap-4 md:overflow-y-auto overflow-x-auto md:w-full ${
                  vertical ? 'flex-row md:flex-col md:pr-1' : `flex-row ${filteredByMain.length > 3 ? '' : 'justify-center'}`
                }`}
              >
                {filteredByMain.map((img) => (
                  <div
                    key={img.id}
                    className={`side-image ${aspect} h-[76px] cursor-pointer ${vertical ? 'md:h-auto md:w-full': 'md:h-[92px]'}`}
                    onClick={() => handleSelectImage(img)}
                  >
                    <Image
                      className={`${aspect} h-full w-full rounded-lg object-cover`}
                      optimized={img.image}
                      alt={img?.title || name}
                      title={img?.title}
                      src={img.img}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {isImageExpanded && (
        <Modal className="w-full max-w-[90vw] max-h-[90vh] bg-transparent" onClose={expandImage}>
          <div className='w-auto h-auto max-w-[90vw] max-h-[90vh]'>
            <div className={`main-image`}>
              <Image
                className={`h-full w-full rounded-lg max-w-[90vw] max-h-[90vh] object-contain`}
                optimized={selectedItem.image}
                src={selectedItem.img}
                title={selectedItem.title}
                alt={selectedItem.title || name}
              />
            </div>

            {moreThanOne && (
              <>
                <div className="absolute top-1/2 -translate-y-1/2 px-2">
                  <Button variant={Colors.rounded} onClick={() => getNewItem(false)}>
                    {Icons({ name: IconsTypes.ARROW_LEFT })}
                  </Button>
                </div>
                <div className="absolute right-0 top-1/2 -translate-y-1/2 px-2">
                  <Button variant={Colors.rounded} onClick={() => getNewItem(true)}>
                    {Icons({ name: IconsTypes.ARROW_RIGHT, iconProps: { className: 'text-black-600' } })}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};